const COMPANY_SANTE_LABELS = {
  actif: 'Active',
  ferme: 'Établissement fermé',
  radie: 'Radiée',
  siret_introuvable: 'SIRET inexistant',
  en_difficulte: 'En difficulté',
  procedure_collective: 'Procédure collective'
}

/**
 * Deux niveaux, la règle du commercial : orange quand une ALERTE pèse sur une
 * entreprise encore en activité, rouge quand elle est INACTIVE (établissement
 * fermé, radiée) ou que son SIRET n'existe pas. L'inactivité l'emporte : une
 * entreprise radiée en liquidation est rouge, pas orange.
 */
const NIVEAU_PAR_SANTE = {
  ferme: 'danger',
  radie: 'danger',
  siret_introuvable: 'danger',
  en_difficulte: 'alerte',
  procedure_collective: 'alerte'
}

/** @returns {'danger'|'alerte'|null} */
export function getCompanySanteNiveau(sante) {
  return NIVEAU_PAR_SANTE[sante] || null
}

/** Toute entreprise qui a un niveau — alerte ou danger — est à risque : une seule liste d'états. */
export function isCompanySanteAtRisk(sante) {
  return Boolean(getCompanySanteNiveau(sante))
}

export function formatCompanySante(sante) {
  return COMPANY_SANTE_LABELS[sante] || sante
}

/** États qu'une alerte BODACC suffit à produire : l'entreprise, elle, est active. */
const SANTE_PAR_ALERTE = ['en_difficulte', 'procedure_collective']

/**
 * Vrai quand l'état ne vient que d'une alerte : l'entreprise est active, et
 * c'est l'alerte, affichée à part, qui dit le problème — comme sur Pappers.
 */
export function estActiveSousAlerte(sante, alertes) {
  return Boolean(alertes?.length) && SANTE_PAR_ALERTE.includes(sante)
}

/**
 * L'état tel que Pappers l'affiche : « Active », puis les alertes à part.
 * Sans alertes (santé calculée avant leur arrivée), le libellé historique
 * reste le seul à dire qu'il y a un problème.
 */
export function formatEtatEntreprise(sante, alertes) {
  return estActiveSousAlerte(sante, alertes) ? COMPANY_SANTE_LABELS.actif : formatCompanySante(sante)
}

/** Le motif le plus précis : la première alerte, sinon l'état. */
export function libelleSante(sante, alertes) {
  return alertes?.[0]?.libelle || formatCompanySante(sante)
}

/** La fiche Pappers de l'entreprise, à partir du SIREN que porte le SIRET. */
export function pappersUrl(siret) {
  const siren = String(siret || '').replace(/\D/g, '').slice(0, 9)
  return siren.length === 9 ? `https://www.pappers.fr/entreprise/${siren}` : null
}

/**
 * `row-company-at-risk` pour toute entreprise à risque (les listes v1 qui ne
 * connaissent qu'un ton continuent de la teinter), plus `--alerte` quand le
 * risque n'est qu'une alerte — la liste des demandes la passe en orange.
 */
export function getCompanySanteRowClass(sante, baseClass = 'clickable-row') {
  if (!isCompanySanteAtRisk(sante)) return baseClass
  const alerte = getCompanySanteNiveau(sante) === 'alerte' ? ' row-company-at-risk--alerte' : ''
  return `${baseClass} row-company-at-risk${alerte}`.trim()
}

export function getSanteBadgeIcon(sante) {
  if (sante === 'actif') return 'fas fa-check-circle'
  if (sante === 'ferme' || sante === 'radie' || sante === 'siret_introuvable') return 'fas fa-exclamation-circle'
  if (sante === 'en_difficulte' || sante === 'procedure_collective') return 'fas fa-exclamation-triangle'
  return 'fas fa-question-circle'
}
